<template>
  <section class="cta dark" id="cta">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-md-6" data-aos="fade-right">
          <section-title
            align="left"
            tone="brand"
            :kicker="t('Any questions?')"
            :title="t('Get in touch')"
            :lede="t('Tell us your specifications on the enquiry form underneath, and a member of our team will respond with a thorough answer as soon as possible.')"
          />
          <ul class="fa-ul">
            <li>
              <span class="fa-li">
                <font-awesome-icon icon="envelope" />
              </span>
              <a :href="emailHref"> {{ email }} </a>
            </li>
            <li>
              <span class="fa-li">
                <font-awesome-icon :icon="['fab', 'whatsapp']" />
              </span>
              {{ t('Office No. / WhatsApp') }}:
              <a :href="whatsappHref" target="_blank" rel="noopener">{{ phone }}</a>
            </li>
            <li>
              <span class="fa-li"> <font-awesome-icon icon="map-signs" /> </span>
              D-01-01, Menara Mitraland<br />
              13A, Jalan PJU 5/1, Kota Damansara<br />
              47810 Petaling Jaya, Selangor<br />
              <a class="small" href="https://g.page/veximus" target="_blank" rel="noopener">{{ t('(View On Map)') }}</a>
            </li>
            <li>
              <span class="fa-li">
                <font-awesome-icon icon="clock" />
              </span>
              {{ t('Business Hours') }}: 9.00 a.m. to 6.00 p.m.<br />
              {{ t('Closed on Weekends and Public Holidays') }}
            </li>
          </ul>
        </div>
        <div class="col-md-6" data-aos="fade-left">
          <form @submit.prevent="submit">
            <div class="mb-3">
              <label class="required">{{ t('Name') }}</label>
              <input type="text" class="form-control" name="name" :placeholder="t('Enter your name...')" v-model="form.name" v-bind:class="{ 'is-invalid': fieldErrors.name }" />
              <div class="invalid-feedback" v-if="fieldErrors.name">
                {{ t(fieldErrors.name) }}
              </div>
            </div>
            <div class="mb-3">
              <label class="required">{{ t('Email address') }}</label>
              <input type="email" class="form-control" name="email" :placeholder="t('Enter your email address...')" v-model="form.email" v-bind:class="{ 'is-invalid': fieldErrors.email }" />
              <div class="invalid-feedback" v-if="fieldErrors.email">
                {{ t(fieldErrors.email) }}
              </div>
            </div>
            <div class="mb-3">
              <label class="required">{{ t('Phone number') }}</label>
              <input type="tel" class="form-control" name="phone" autocomplete="tel" :placeholder="t('Enter your phone number...')" v-model="form.phone" v-bind:class="{ 'is-invalid': fieldErrors.phone }" />
              <div class="invalid-feedback" v-if="fieldErrors.phone">
                {{ t(fieldErrors.phone) }}
              </div>
            </div>
            <div class="mb-3">
              <label>{{ t('Message') }}</label>
              <textarea class="form-control" name="content" rows="5" :placeholder="t('(Optional) Leave us your message...')" v-model="form.content"></textarea>
            </div>
            <div class="cta-check">
              <div v-show="sent && !checking" class="cta-check-wait" role="status">
                <font-awesome-icon icon="check" />
                <span>{{ t('Your message has been sent successfully.') }}</span>
              </div>
              <div v-show="checkVisible && awaitingWidget" class="cta-check-wait" role="status">
                <font-awesome-icon icon="spinner" spin />
                <span>{{ t('Verifying you are human...') }}</span>
              </div>
              <div ref="turnstile" class="cta-turnstile"></div>
              <div class="invalid-feedback" v-show="turnstileError">
                {{ turnstileError }}
              </div>
            </div>
            <div class="invalid-feedback cta-form-error" v-show="formError" role="alert">
              {{ formError }}
            </div>
            <app-button
              type="submit"
              tone="dark"
              icon="paper-plane"
              :loading="sending || awaitingWidget"
              :disabled="sending || checking"
            >
              {{ t('Send Message') }}
            </app-button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.cta {
  background: linear-gradient(90deg, $primary, $accent-blue);
  color: $on-dark-strong;
  position: relative;

  &::before {
    background: url('/images/home/hero/bg.png') no-repeat top;
    background-size: cover;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  /* The kicker, title and lede are a SectionTitle now, so the block is the same
     one every other section on the site uses. It sits above the glass layer. */
  .section-title {
    position: relative;
    z-index: 1;
  }

  .fa-ul {
    margin-left: 3.5em;

    .fa-li {
      left: -3em;
    }

    a {
      color: $on-dark-strong;
      display: inline-block;
      text-decoration: underline;
      text-underline-offset: 0.15em;
      transition: color $dur-quick $ease, transform $dur-quick $ease;

      /* Light blue rather than the footer's hairline wipe: these are already
         underlined, so the hover cue is the tint and the nudge east. */
      &:hover,
      &:focus-visible {
        color: $dark-accent;
        transform: translateX(3px);
      }
    }

    @media (max-width: 767.98px) {
      margin-bottom: 3rem;
    }
  }

  form {
    position: relative;
    z-index: 5;

    label {
      font-size: 0.9rem;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }

    /* The global reset in _reset.scss kills outline and box-shadow on every
       :focus, so the only channel left for focus feedback on the fields is the
       border itself. */
    .form-control {
      transition: border-color $dur-quick $ease;

      &:hover {
        border-color: $border-primary;
      }

      &:focus {
        border-color: $primary;
      }
    }

    /* The submit button owns its own hover now (AppButton: the lift, the arrow
       nudge and the dark-ground shadow), so this block keeps only the field
       feedback above. */

    .invalid-feedback {
      background: #dc3545;
      border-radius: $r-xs;
      color: $on-dark-strong;
      display: inline-block;
      padding: 0rem 0.25rem;
      width: auto;
    }

    .cta-check {
        .invalid-feedback {
            margin-bottom: 1.5rem;
        }
    }

    .cta-turnstile {
      width: 100%;
      margin-bottom: .5rem;
    }

    .cta-check-wait {
      align-items: center;
      background: $ground;
      border: 1px solid $line;
      border-radius: 0.15rem;
      color: $primary;
      display: flex;
      font-size: 0.9rem;
      font-weight: 500;
      gap: 0.6rem;
      justify-content: center;
      margin-bottom: 1rem;
      min-height: 65px;
      padding: 0.75rem 1rem;
      text-align: center;

      span {
        min-width: 0;
      }
    }

    .cta-form-error {
      margin-bottom: 1rem;
    }
  }

}
</style>

<script>
import { faCheck, faClock, faEnvelope, faMapSigns, faPaperPlane, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import { trackLead } from '@/analytics';
import { mount, reset, token } from '@/turnstile';
import { whatsappHref } from '@/whatsapp';

FontAwesomeLibrary.add(faCheck, faClock, faEnvelope, faMapSigns, faPaperPlane, faSpinner);

export default {
  components: {
    AppButton,
    SectionTitle
  },

  data() {
    return {
      form: {
        name: null,
        email: null,
        phone: null,
        content: null
      },
      fieldErrors: {},
      formError: '',
      sent: false,
      sending: false,
      turnstileError: '',
      /* True from the click until the request has settled. Holds the button
         disabled, including while the checkbox is on screen. */
      checking: false,
      /* The button spinner. On from the click until the checkbox is actually
         there, then again for the request itself. */
      awaitingWidget: false,
      /* The row above the button. Held back a moment so a check that passes
         without a checkbox never flashes an empty bar. */
      checkVisible: false
    };
  },

  computed: {
    /* The published address, from the shared business block, so the two places
       the site prints it cannot drift apart. */
    emailHref() {
      return 'mailto:' + this.$page.props.business.email;
    },

    email() {
      return this.$page.props.business.email;
    },

    /* The same office number the footer dials. The digits are what wa.me
       accepts, so the printed spacing never becomes a second number. */
    phone() {
      return this.$page.props.business.phone;
    },

    whatsappHref() {
      return whatsappHref(this.phone, this.$page.props.locale, this.$page.props.referral);
    }
  },

  setup() {
    /* The message box starts empty on every page. It used to take a per-page
       opening line, which made this one shared form behave differently
       depending on the page it sat on. The homepage is the reference: empty
       field, the placeholder is the only prompt. */
    const { t } = useI18n({
      messages: {
        'en-MY': {
          '(Optional) Leave us your message...': '(Optional) Leave us your message...',
          '(View On Map)': '(View On Map)',
          'Any questions?': 'Any questions?',
          'Business Hours': 'Business Hours',
          'Closed on Weekends and Public Holidays': 'Closed on Weekends and Public Holidays',
          'Email address': 'Email address',
          'Enter your email address...': 'Enter your email address...',
          'Enter your name...': 'Enter your name...',
          'Office No. / WhatsApp': 'Office No. / WhatsApp',
          'Enter your phone number...': 'Enter your phone number...',
          'Phone number': 'Phone number',
          'Tell us your specifications on the enquiry form underneath, and a member of our team will respond with a thorough answer as soon as possible.': 'Tell us your specifications on the enquiry form underneath, and a member of our team will respond with a thorough answer as soon as possible.',
          'Send Message': 'Send Message',
          'Your message has been sent successfully.': 'Your message has been sent successfully.',
          'Too many messages were sent from here. Please try again later.': 'Too many messages were sent from here. Please try again later.',
          'The message could not be sent. Please try again.': 'The message could not be sent. Please try again.',
          'This page has expired. Refresh it and send the message again.': 'This page has expired. Refresh it and send the message again.',
          'Contact name cannot be empty.': 'Contact name cannot be empty.',
          'Email address cannot be empty.': 'Email address cannot be empty.',
          'Invalid email address.': 'Invalid email address.',
          'Phone number cannot be empty.': 'Phone number cannot be empty.',
          'Invalid phone number.': 'Invalid phone number.',
          Message: 'Message',
          Name: 'Name'
        },
        'zh-MY': {
          '(Optional) Leave us your message...': '（可选）给我们留言...',
          '(View On Map)': '（查看地图）',
          'Any questions?': '还有疑问？',
          'Business Hours': '营业时间',
          'Closed on Weekends and Public Holidays': '周末及公众假期休息',
          'Email address': '电邮地址',
          'Enter your email address...': '输入你的电邮...',
          'Enter your name...': '输入你的名字...',
          'Office No. / WhatsApp': '办公室号码 / WhatsApp',
          'Enter your phone number...': '输入你的电话号码...',
          'Phone number': '电话号码',
          'Tell us your specifications on the enquiry form underneath, and a member of our team will respond with a thorough answer as soon as possible.': '我们的团队很乐意协助您。只要留下您的信息与需求，我们收到之后就会尽快与您联系，为您解答任何疑问，并安排下一步的行动，让您清楚接下来的方向。',
          'Send Message': '发送信息',
          'Your message has been sent successfully.': '您的信息已成功发送。',
          'Too many messages were sent from here. Please try again later.': '这里发送的信息太多了，请稍后再试。',
          'The message could not be sent. Please try again.': '信息未能发送，请再试一次。',
          'This page has expired. Refresh it and send the message again.': '此页面已过期。请刷新后再发送。',
          'Contact name cannot be empty.': '名称不能为空。',
          'Email address cannot be empty.': '电邮地址不能为空。',
          'Invalid email address.': '电邮地址无效。',
          'Phone number cannot be empty.': '电话号码不能为空。',
          'Invalid phone number.': '电话号码无效。',
          Message: '信息',
          Name: '名称'
        },
        'ms-MY': {
          '(Optional) Leave us your message...': '(Pilihan) Tinggalkan mesej anda...',
          '(View On Map)': '(Peta Lokasi)',
          'Any questions?': 'Ada sebarang pertanyaan?',
          'Business Hours': 'Waktu operasi',
          'Closed on Weekends and Public Holidays': 'Tutup setiap hujung minggu dan cuti umum',
          'Email address': 'Alamat emel',
          'Enter your email address...': 'Masukkan alamat emel anda...',
          'Enter your name...': 'Masukkan nama anda...',
          'Office No. / WhatsApp': 'No. Pejabat / WhatsApp',
          'Enter your phone number...': 'Masukkan nombor telefon anda...',
          'Phone number': 'Nombor telefon',
          'Tell us your specifications on the enquiry form underneath, and a member of our team will respond with a thorough answer as soon as possible.': 'Tinggalkan mesej kepada kami dan pasukan kami akan membacanya, kemudian menghubungi anda semula dengan jawapan yang jelas dan lengkap secepat mungkin.',
          'Send Message': 'Hantar Mesej',
          'Your message has been sent successfully.': 'Mesej anda telah berjaya dihantar.',
          'Too many messages were sent from here. Please try again later.': 'Terlalu banyak mesej dihantar dari sini. Sila cuba lagi kemudian.',
          'The message could not be sent. Please try again.': 'Mesej tidak dapat dihantar. Sila cuba lagi.',
          'This page has expired. Refresh it and send the message again.': 'Halaman ini telah tamat tempoh. Muat semula dan hantar mesej sekali lagi.',
          'Contact name cannot be empty.': 'Nama tidak boleh kosong.',
          'Email address cannot be empty.': 'Alamat emel tidak boleh kosong.',
          'Invalid email address.': 'Alamat emel tidak sah.',
          'Phone number cannot be empty.': 'Nombor telefon tidak boleh kosong.',
          'Invalid phone number.': 'Nombor telefon tidak sah.',
          Message: 'Mesej',
          Name: 'Nama'
        }
      }
    });

    return { t };
  },

  mounted() {
    this.alive = true;

    const sitekey = this.$page.props.turnstile && this.$page.props.turnstile.sitekey;

    if (sitekey) {
      mount(this.$refs.turnstile, 'contact', sitekey);
    }
  },

  beforeUnmount() {
    /* A visit away settles the check on the widget that just left. Resetting
       from here would hit the widget the next page has already mounted. */
    this.alive = false;
    this.clearCheckTimer();
  },

  methods: {
    clearCheckTimer() {
      if (this.checkTimer) {
        clearTimeout(this.checkTimer);
        this.checkTimer = null;
      }
    },

    async submit() {
      if (this.checking || this.sending) {
        return;
      }

      this.turnstileError = '';
      this.formError = '';
      this.fieldErrors = {};
      this.sent = false;

      const sitekey = this.$page.props.turnstile && this.$page.props.turnstile.sitekey;
      let turnstileToken = '';

      if (sitekey) {
        this.checking = true;
        this.awaitingWidget = true;
        this.clearCheckTimer();
        this.checkTimer = setTimeout(() => {
          if (this.checking && this.alive) {
            this.checkVisible = true;
          }
        }, 200);

        try {
          turnstileToken = await token('contact', () => {
            if (!this.alive) {
              return;
            }

            /* The checkbox is up. The button rests so the tick is the next
               click, and the row is open for the widget. */
            this.awaitingWidget = false;
            this.checkVisible = true;
            this.clearCheckTimer();
          });
        } catch (error) {
          if (!this.alive) {
            return;
          }

          this.checking = false;
          this.awaitingWidget = false;
          this.checkVisible = false;
          this.clearCheckTimer();

          /* Switching language redraws the widget and drops the open check on
             purpose. That is not a failure, and resetting here would hit the
             widget the redraw just mounted. */
          if (error && error.reason === 'abandoned') {
            return;
          }

          this.turnstileError = this.t('The anti-bot check failed. Please try again.');
          reset('contact');

          return;
        }

        if (!this.alive) {
          return;
        }

        /* The token is already in hand. Fold the checkbox away and let the
           button carry the rest of the send. The page itself does not navigate:
           the answer is shown in this column. */
        this.awaitingWidget = true;
        this.checkVisible = false;
        this.clearCheckTimer();
        reset('contact');
      }

      if (!this.alive) {
        return;
      }

      this.sending = true;

      const eventId = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : 'evt-' + Date.now().toString(36) + Math.random().toString(36).slice(2);
      const leadName = this.form.name;
      const leadEmail = this.form.email;
      const leadPhone = this.form.phone;

      try {
        await window.axios.post(route('message'), {
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone,
          content: this.form.content,
          event_id: eventId,
          event_source_url: window.location.href,
          'cf-turnstile-response': turnstileToken
        }, {
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': this.csrfToken()
          }
        });
      } catch (error) {
        if (!this.alive) {
          return;
        }

        this.settle();
        this.applyFailure(error);

        return;
      }

      if (!this.alive) {
        return;
      }

      trackLead(eventId, leadEmail, leadName, leadPhone);
      this.form.name = null;
      this.form.email = null;
      this.form.phone = null;
      this.form.content = null;
      this.fieldErrors = {};
      this.formError = '';
      this.turnstileError = '';
      this.sent = true;
      this.settle();
    },

    settle() {
      this.checking = false;
      this.sending = false;
      this.awaitingWidget = false;
      this.checkVisible = false;
      this.clearCheckTimer();
      reset('contact');
    },

    applyFailure(error) {
      const response = error && error.response;
      const status = response && response.status;
      const errors = response && response.data && response.data.errors;

      if (status === 422 && errors) {
        if (errors['cf-turnstile-response']) {
          this.turnstileError = this.t('The anti-bot check failed. Please try again.');
        }

        this.fieldErrors = this.firstErrors(errors);
        delete this.fieldErrors['cf-turnstile-response'];

        return;
      }

      if (status === 429) {
        this.formError = this.t('Too many messages were sent from here. Please try again later.');

        return;
      }

      if (status === 419) {
        this.formError = this.t('This page has expired. Refresh it and send the message again.');

        return;
      }

      this.formError = this.t('The message could not be sent. Please try again.');
    },

    firstErrors(errors) {
      const out = {};

      Object.keys(errors).forEach((key) => {
        const value = errors[key];

        out[key] = Array.isArray(value) ? value[0] : value;
      });

      return out;
    },

    csrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]');

      return meta ? meta.getAttribute('content') : '';
    }
  }
};
</script>
